import { useEffect, useState, type ReactNode } from "react"
import { router } from "@inertiajs/react"
import { Lock } from "lucide-react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select, type SelectOption } from "@/components/ui/select"
import { Badge } from "@/components/ui/badge"
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "@/components/ui/dialog"
import { QueryParamEditor } from "@/components/domain/query-param-editor"
import type { Endpoint, HttpMethod } from "@/types/endpoint"

type Props = {
  endpoint: Endpoint | null            // null when creating a new draft
  httpMethods: HttpMethod[]
  isDraft: boolean                      // true when this is a new in-memory record
  onSaved: () => void                   // called after successful save
  onCancel: () => void                  // discard a draft / unsaved edits
  onDelete: () => void                  // delete an existing endpoint
}

const BLANK = {
  name: "",
  key: "",
  httpMethod: "GET" as HttpMethod,
  pathTemplate: "/",
  timeoutMs: "5000",
  cacheTtlSeconds: "0",
  querySchemaJson: "[]",
}

// Auto-derive a slug from a free-text name. Lowercase, replace non-alphanum
// with underscore, collapse runs, trim leading/trailing underscores.
function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
}

export function EndpointForm({ endpoint, httpMethods, isDraft, onSaved, onCancel, onDelete }: Props) {
  const [fields, setFields] = useState(BLANK)
  const [submitting, setSubmitting] = useState(false)
  // Track whether the user has hand-edited the key. While false, typing in
  // Name auto-fills the key. After the user touches Key directly, stop syncing.
  const [keyTouched, setKeyTouched] = useState(false)
  const [schemaInfoOpen, setSchemaInfoOpen] = useState(false)

  useEffect(() => {
    if (endpoint) {
      setFields({
        name: endpoint.name,
        key: endpoint.key,
        httpMethod: endpoint.httpMethod,
        pathTemplate: endpoint.pathTemplate,
        timeoutMs: endpoint.timeoutMs?.toString() ?? "5000",
        cacheTtlSeconds: endpoint.cacheTtlSeconds?.toString() ?? "0",
        querySchemaJson: JSON.stringify(endpoint.querySchema ?? [], null, 2),
      })
      setKeyTouched(true) // existing endpoints — don't auto-rewrite the key
    } else {
      setFields(BLANK)
      setKeyTouched(false)
    }
  }, [endpoint?.id, isDraft])

  const keyImmutable = !isDraft && !!endpoint?.keyImmutable

  const handleNameChange = (val: string) => {
    setFields((f) => ({ ...f, name: val, key: keyTouched ? f.key : slugify(val) }))
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    const payload = {
      endpoint: {
        name: fields.name,
        key: fields.key,
        http_method: fields.httpMethod,
        path_template: fields.pathTemplate,
        timeout_ms: fields.timeoutMs ? parseInt(fields.timeoutMs, 10) : null,
        cache_ttl_seconds: fields.cacheTtlSeconds ? parseInt(fields.cacheTtlSeconds, 10) : 0,
        query_schema_json: fields.querySchemaJson,
      }
    }
    const onFinish = () => { setSubmitting(false); onSaved() }
    if (isDraft || !endpoint) {
      router.post("/endpoints", payload, { preserveScroll: true, onFinish })
    } else {
      router.patch(`/endpoints/${endpoint.id}`, payload, { preserveScroll: true, onFinish })
    }
  }

  const methodOptions: SelectOption<HttpMethod>[] = httpMethods.map((m) => ({ value: m, label: m }))

  return (
    <>
    <Card className="overflow-hidden p-0">
      <form onSubmit={handleSubmit} className="flex flex-col">
        <header className="border-b border-border px-5 py-4">
          <h2 className="text-base font-semibold text-app-text">
            {isDraft ? "New endpoint" : "Endpoint details"}
          </h2>
          <p className="mt-0.5 text-sm text-app-muted">
            {isDraft
              ? "Configure a new partner endpoint for your skills to call."
              : "Edit the selected endpoint configuration."}
          </p>
        </header>

        <div className="space-y-6 p-5">
          <Section number={1} title="Basics">
            <div>
              <label className="mb-1.5 block text-xs uppercase tracking-wide text-app-muted">Name</label>
              <Input
                type="text"
                required
                value={fields.name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="Coin balance lookup"
                className="w-full"
              />
              <p className="mt-1 text-xs text-app-muted">Human label shown in lists. Free text.</p>
            </div>

            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <label className="text-xs uppercase tracking-wide text-app-muted">Key (slug)</label>
                {keyImmutable && (
                  <Badge tone="gold" className="gap-1 px-2 py-0.5 text-[10px]">
                    <Lock size={10} strokeWidth={2.5} />
                    Locked · {endpoint?.referenceCount ?? 0} skill{(endpoint?.referenceCount ?? 0) === 1 ? "" : "s"}
                  </Badge>
                )}
              </div>
              <Input
                type="text"
                required
                value={fields.key}
                onChange={(e) => { setKeyTouched(true); setFields((f) => ({ ...f, key: e.target.value })) }}
                disabled={keyImmutable}
                pattern="[a-z0-9_]+"
                placeholder="coin_balance"
                className="w-full font-mono"
              />
              <p className="mt-1 text-xs text-app-muted">
                {keyImmutable
                  ? "Slugs are locked once a skill references them. Detach all references to rename, or delete and recreate."
                  : "Lowercase letters, digits, underscores. Referenced by skills via call_company_endpoint."}
              </p>
            </div>
          </Section>

          <Section number={2} title="Request">
            <div className="grid gap-4 sm:grid-cols-[120px_minmax(0,1fr)]">
              <div>
                <label className="mb-1.5 block text-xs uppercase tracking-wide text-app-muted">HTTP method</label>
                <Select<HttpMethod>
                  value={methodOptions.find((o) => o.value === fields.httpMethod) ?? methodOptions[0]}
                  options={methodOptions}
                  onChange={(o) => o && setFields((f) => ({ ...f, httpMethod: o.value }))}
                  isSearchable={false}
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs uppercase tracking-wide text-app-muted">Path template</label>
                <Input
                  type="text"
                  required
                  value={fields.pathTemplate}
                  onChange={(e) => setFields((f) => ({ ...f, pathTemplate: e.target.value }))}
                  placeholder="/api/v1/users/{source_key}/coins"
                  className="w-full font-mono"
                />
              </div>
            </div>
            <p className="text-xs text-app-muted">
              Use <span className="font-mono text-app-text">{"{source_key}"}</span> to interpolate the appuser's id.
            </p>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1.5 block text-xs uppercase tracking-wide text-app-muted">Timeout (ms)</label>
                <Input
                  type="number"
                  min="100"
                  value={fields.timeoutMs}
                  onChange={(e) => setFields((f) => ({ ...f, timeoutMs: e.target.value }))}
                  className="w-full"
                />
              </div>
              <div>
                <label className="mb-1.5 block text-xs uppercase tracking-wide text-app-muted">Cache TTL (s)</label>
                <Input
                  type="number"
                  min="0"
                  value={fields.cacheTtlSeconds}
                  onChange={(e) => setFields((f) => ({ ...f, cacheTtlSeconds: e.target.value }))}
                  className="w-full"
                />
              </div>
            </div>
          </Section>

          <Section
            number={3}
            title="Query schema"
            action={
              <button
                type="button"
                onClick={() => setSchemaInfoOpen(true)}
                className="text-xs text-app-muted underline decoration-dotted hover:text-app-text"
              >
                Learn more
              </button>
            }
          >
            <QueryParamEditor
              value={fields.querySchemaJson}
              onChange={(v) => setFields((f) => ({ ...f, querySchemaJson: v }))}
              httpMethod={fields.httpMethod}
            />
          </Section>
        </div>

        <footer className="flex items-center justify-between border-t border-border px-5 py-3">
          <div>
            {!isDraft && endpoint && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onDelete}
                disabled={endpoint.referenceCount > 0}
                title={endpoint.referenceCount > 0 ? `Referenced by ${endpoint.referenceCount} skill(s) — detach to enable delete` : undefined}
                className="border-status-error/40 text-status-error hover:bg-status-error/10"
              >
                Delete
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
              Cancel
            </Button>
            <Button type="submit" size="sm" disabled={submitting}>
              {submitting ? "Saving…" : "Save changes"}
            </Button>
          </div>
        </footer>
      </form>
    </Card>

    <Dialog open={schemaInfoOpen} onClose={() => setSchemaInfoOpen(false)} size="lg">
        <DialogHeader
          title="Request Params"
          subtitle="A whitelist and required-list for query params on GETs and JSON body params on writes."
        />
        <DialogBody>
          <section>
            <h3 className="mb-1 text-sm font-semibold text-app-text">Why it exists</h3>
            <p className="text-sm text-app-muted">
              The AI agent decides which params to send when it calls{" "}
              <span className="font-mono text-app-text">call_company_endpoint(endpoint_key: …)</span>.
              The schema is your contract: only listed params reach your API,
              and missing required params fail fast instead of hitting your
              backend with bad input. Path-template tokens like{" "}
              <span className="font-mono text-app-text">{"{source_key}"}</span>{" "}
              go in the path itself. GET params go in the query string; POST,
              PUT, and PATCH params go in the JSON body unless a field explicitly
              sets <span className="font-mono text-app-text">location: query</span>.
            </p>
          </section>

          <section>
            <h3 className="mb-1 text-sm font-semibold text-app-text">Format</h3>
            <p className="mb-2 text-sm text-app-muted">JSON array. Each entry is either:</p>
            <ul className="ml-4 list-disc space-y-1 text-sm text-app-muted">
              <li>A string — shorthand for an optional param: <span className="font-mono text-app-text">"limit"</span></li>
              <li>
                An object — supports a JSON-Schema-style subset (each key is optional except <span className="font-mono text-app-text">name</span>):
                <ul className="ml-5 mt-1 list-[circle] space-y-0.5">
                  <li><span className="font-mono text-app-text">name</span> — snake_case param name</li>
                  <li><span className="font-mono text-app-text">type</span> — <span className="font-mono">string</span> | <span className="font-mono">integer</span> | <span className="font-mono">number</span> | <span className="font-mono">boolean</span></li>
                  <li><span className="font-mono text-app-text">format</span> (string only) — <span className="font-mono">date-time</span> | <span className="font-mono">date</span> | <span className="font-mono">uuid</span> | <span className="font-mono">email</span> | <span className="font-mono">uri</span></li>
                  <li><span className="font-mono text-app-text">enum</span> — array of allowed values (must match <span className="font-mono">type</span>)</li>
                  <li><span className="font-mono text-app-text">pattern</span> (string only) — regex string the value must match</li>
                  <li><span className="font-mono text-app-text">minimum</span> / <span className="font-mono text-app-text">maximum</span> (numeric only)</li>
                  <li><span className="font-mono text-app-text">required</span> — true to fail-fast when missing</li>
                  <li><span className="font-mono text-app-text">default</span> — shown to the AI as a hint (NOT auto-injected)</li>
                  <li><span className="font-mono text-app-text">description</span> — human / AI-facing one-liner</li>
                  <li><span className="font-mono text-app-text">location</span> — optional override: <span className="font-mono">query</span> or <span className="font-mono">body</span></li>
                </ul>
              </li>
            </ul>
            <p className="mt-2 text-xs text-app-muted">
              Strict subset — anything outside this list (e.g. <span className="font-mono">oneOf</span>, <span className="font-mono">$ref</span>) is rejected on save. See the full JSON Schema reference at{" "}
              <a href="https://json-schema.org/understanding-json-schema/reference" target="_blank" rel="noreferrer" className="underline decoration-dotted hover:text-app-text">json-schema.org</a> for what we may add later.
            </p>
          </section>

          <section>
            <h3 className="mb-2 text-sm font-semibold text-app-text">Examples</h3>

            <div className="space-y-3">
              <div className="rounded-control border border-border bg-app-bg p-3">
                <div className="mb-1 text-xs uppercase tracking-wide text-app-muted">No params (most lookups)</div>
                <pre className="overflow-x-auto font-mono text-xs text-app-text">[]</pre>
              </div>

              <div className="rounded-control border border-border bg-app-bg p-3">
                <div className="mb-1 text-xs uppercase tracking-wide text-app-muted">Required ISO8601 date-time</div>
                <pre className="overflow-x-auto font-mono text-xs text-app-text">{`[
  { "name": "since",
    "type": "string",
    "format": "date-time",
    "required": true,
    "description": "Range start (ISO8601 UTC)." }
]`}</pre>
                <p className="mt-2 text-xs text-app-muted">
                  AI must supply a valid date-time or the call fails before reaching you with a structured error
                  the LLM can self-correct on.
                </p>
              </div>

              <div className="rounded-control border border-border bg-app-bg p-3">
                <div className="mb-1 text-xs uppercase tracking-wide text-app-muted">POST/PATCH body params</div>
                <pre className="overflow-x-auto font-mono text-xs text-app-text">{`[
  { "name": "weight_lbs", "type": "number", "minimum": 50, "maximum": 1000 },
  { "name": "dry_run", "type": "boolean", "location": "query" }
]`}</pre>
                <p className="mt-2 text-xs text-app-muted">
                  On POST, PUT, and PATCH, params default to the JSON body. Use{" "}
                  <span className="font-mono">location: "query"</span> only for true URL query params.
                </p>
              </div>

              <div className="rounded-control border border-border bg-app-bg p-3">
                <div className="mb-1 text-xs uppercase tracking-wide text-app-muted">Mix of legacy + rich</div>
                <pre className="overflow-x-auto font-mono text-xs text-app-text">{`[
  "event_name",
  { "name": "from", "type": "string", "format": "date-time" },
  { "name": "to",   "type": "string", "format": "date-time" }
]`}</pre>
                <p className="mt-2 text-xs text-app-muted">
                  Plain string entries still work — useful when you don't need any constraints beyond "this name is allowed."
                </p>
              </div>
            </div>
          </section>

          <section>
            <h3 className="mb-1 text-sm font-semibold text-app-text">Behavior at request time</h3>
            <ul className="ml-4 list-disc space-y-1 text-sm text-app-muted">
              <li>Listed params are passed through (with type coercion when <span className="font-mono">type</span> is set).</li>
              <li>Unlisted params are silently dropped.</li>
              <li>A missing <span className="font-mono">required</span> param raises{" "}
                <span className="font-mono text-app-text">MissingQueryParam</span> before any HTTP request.
              </li>
              <li>A value that violates the schema raises{" "}
                <span className="font-mono text-app-text">InvalidQueryParam</span> with a descriptive
                message — the AI sees it as a structured tool error and self-corrects.
              </li>
              <li>The schema is also surfaced to the AI in the system prompt so it tries to send valid values on the FIRST call.</li>
            </ul>
          </section>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="ghost" onClick={() => setSchemaInfoOpen(false)}>
            Got it
          </Button>
        </DialogFooter>
      </Dialog>
    </>
  )
}

function Section({
  number,
  title,
  action,
  children,
}: {
  number: number
  title: string
  action?: ReactNode
  children: ReactNode
}) {
  return (
    <section className="space-y-3">
      <header className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="flex h-5 w-5 items-center justify-center rounded-full bg-app-panel3 text-[11px] font-semibold text-app-muted">
            {number}
          </span>
          <h3 className="text-base font-semibold text-app-text">{title}</h3>
        </div>
        {action}
      </header>
      <div className="space-y-3">{children}</div>
    </section>
  )
}
